// Step 5 of the client-side X12 pipeline (867 variant): walks one
// transaction's flat body segments into the loop structure a human reader
// thinks in — header (BPT/CUR/REF/DTM), Name loops, PTD-style
// product-transfer-detail loops (each nesting QTY/AMT/CTP/REF/DTM), and a
// CTT/AMT summary.

import type {EdiSegment} from './tokenizer';
import type {EdiEnvelope, EdiGroup, EdiTransaction} from './envelope';

export interface Header867 {
  bpt?: EdiSegment;
  cur?: EdiSegment;
  refs: EdiSegment[];
  dtm: EdiSegment[];
  other: EdiSegment[];
}

export interface NameLoop867 {
  n1: EdiSegment;
  n2?: EdiSegment;
  n3?: EdiSegment;
  n4?: EdiSegment;
  n5?: EdiSegment;
  n6?: EdiSegment;
  n9: EdiSegment[];
  refs: EdiSegment[];
  per?: EdiSegment;
  other: EdiSegment[];
}

export interface ItemLoop867 {
  ptd: EdiSegment;
  qty: EdiSegment[];
  amt: EdiSegment[];
  ctp?: EdiSegment;
  refs: EdiSegment[];
  dtm: EdiSegment[];
  other: EdiSegment[];
}

export interface Summary867 {
  ctt?: EdiSegment;
  amt: EdiSegment[];
  other: EdiSegment[];
}

export interface Parsed867 {
  header: Header867;
  nameLoops: NameLoop867[];
  items: ItemLoop867[];
  summary: Summary867;
}

function attachHeaderSegment(header: Header867, s: EdiSegment): void {
  switch (s.id) {
    case 'CUR':
      header.cur = s;
      break;
    case 'REF':
      header.refs.push(s);
      break;
    case 'DTM':
      header.dtm.push(s);
      break;
    default:
      header.other.push(s);
      break;
  }
}

function attachNameLoopSegment(loop: NameLoop867, s: EdiSegment): void {
  switch (s.id) {
    case 'N2':
      loop.n2 = s;
      break;
    case 'N3':
      loop.n3 = s;
      break;
    case 'N4':
      loop.n4 = s;
      break;
    case 'N5':
      loop.n5 = s;
      break;
    case 'N6':
      loop.n6 = s;
      break;
    case 'N9':
      loop.n9.push(s);
      break;
    case 'REF':
      loop.refs.push(s);
      break;
    case 'PER':
      loop.per = s;
      break;
    default:
      loop.other.push(s);
      break;
  }
}

function attachItemSegment(item: ItemLoop867, s: EdiSegment): void {
  switch (s.id) {
    case 'QTY':
      item.qty.push(s);
      break;
    case 'CTP':
      item.ctp = s;
      break;
    case 'REF':
      item.refs.push(s);
      break;
    case 'DTM':
      item.dtm.push(s);
      break;
    default:
      item.other.push(s);
      break;
  }
}

function emptyHeader(): Header867 {
  return {refs: [], dtm: [], other: []};
}

function emptyItem(ptd: EdiSegment): ItemLoop867 {
  return {ptd, qty: [], amt: [], refs: [], dtm: [], other: []};
}

type Phase = 'header' | 'name' | 'item' | 'summary';

/**
 * Walks one 867 transaction's flat body segments (ST/SE excluded) into its
 * loop structure. Never throws — a segment that doesn't fit anywhere
 * sensible in the current loop is bucketed into that loop's `other` list
 * rather than the walk aborting; the validator is what reports on a
 * document that's missing or misordered.
 */
export function parse867Segments(segments: EdiSegment[]): Parsed867 {
  const header = emptyHeader();
  const nameLoops: NameLoop867[] = [];
  const items: ItemLoop867[] = [];
  const summary: Summary867 = {amt: [], other: []};

  let phase: Phase = 'header';
  let currentNameLoop: NameLoop867 | undefined;
  let currentItem: ItemLoop867 | undefined;

  for (const s of segments) {
    switch (s.id) {
      case 'BPT':
        header.bpt = s;
        break;

      case 'N1':
        phase = 'name';
        currentNameLoop = {n1: s, n9: [], refs: [], other: []};
        nameLoops.push(currentNameLoop);
        break;

      case 'N2':
      case 'N3':
      case 'N4':
      case 'N5':
      case 'N6':
      case 'N9':
        if (phase === 'name' && currentNameLoop) {
          attachNameLoopSegment(currentNameLoop, s);
        } else if (phase === 'header') {
          header.other.push(s);
        }
        break;

      case 'PTD':
        phase = 'item';
        currentNameLoop = undefined;
        currentItem = emptyItem(s);
        items.push(currentItem);
        break;

      case 'CTT':
        phase = 'summary';
        summary.ctt = s;
        currentItem = undefined;
        currentNameLoop = undefined;
        break;

      case 'AMT':
        if (phase === 'item' && currentItem) {
          currentItem.amt.push(s);
        } else if (phase === 'summary') {
          summary.amt.push(s);
        } else if (phase === 'header') {
          header.other.push(s);
        } else if (phase === 'name' && currentNameLoop) {
          currentNameLoop.other.push(s);
        }
        break;

      default:
        if (phase === 'header') {
          attachHeaderSegment(header, s);
        } else if (phase === 'name' && currentNameLoop) {
          attachNameLoopSegment(currentNameLoop, s);
        } else if (phase === 'item' && currentItem) {
          attachItemSegment(currentItem, s);
        } else if (phase === 'summary') {
          summary.other.push(s);
        }
        break;
    }
  }

  return {header, nameLoops, items, summary};
}

export interface EdiTransactionWithParsed867 extends EdiTransaction {
  /** Left undefined for a transaction whose ST01 isn't "867" — it keeps only
   * its raw segment list, for read-only display. */
  parsed867?: Parsed867;
}

export interface EdiGroupWithParsed867 extends Omit<EdiGroup, 'transactions'> {
  transactions: EdiTransactionWithParsed867[];
}

export interface EdiEnvelopeWithParsed867 extends Omit<EdiEnvelope, 'groups'> {
  groups: EdiGroupWithParsed867[];
}

/**
 * Parses every 867 transaction in an envelope tree (step 2's output),
 * attaching `parsed867` to each. A non-867 transaction is passed through
 * unchanged, `parsed867` left undefined.
 */
export function attachParsed867(
  envelope: EdiEnvelope
): EdiEnvelopeWithParsed867 {
  return {
    ...envelope,
    groups: envelope.groups.map(group => ({
      ...group,
      transactions: group.transactions.map(transaction => ({
        ...transaction,
        parsed867:
          transaction.transactionSetCode === '867'
            ? parse867Segments(transaction.segments)
            : undefined,
      })),
    })),
  };
}
